import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { mdmApi, isFinalCommandStatus } from '../services/mdmApi'
import { getTenantId } from '../config/tenant'

const visiblePoll = (ms) => () =>
  typeof document !== 'undefined' && document.hidden ? false : ms

/** Resumo da frota mobile do tenant (aba "Mobile"). */
export const useMdmFleet = () => {
  const tenantId = getTenantId()
  return useQuery({
    queryKey: ['mdm-fleet', tenantId],
    queryFn: () => mdmApi.getFleet(tenantId),
    enabled: !!tenantId,
    refetchInterval: visiblePoll(10000),
    refetchIntervalInBackground: false,
  })
}

/** Estado MDM de um device (header + abas do console). */
export const useMdmState = (deviceId) =>
  useQuery({
    queryKey: ['mdm-state', deviceId],
    queryFn: () => mdmApi.getState(deviceId),
    enabled: !!deviceId,
    refetchInterval: visiblePoll(10000),
    refetchIntervalInBackground: false,
  })

/** Histórico de comandos — acelera o polling enquanto houver comando em andamento. */
export const useMdmCommands = (deviceId, limit = 50) =>
  useQuery({
    queryKey: ['mdm-commands', deviceId, limit],
    queryFn: () => mdmApi.getCommands(deviceId, limit),
    enabled: !!deviceId,
    refetchInterval: (query) => {
      if (typeof document !== 'undefined' && document.hidden) return false
      const data = query.state.data
      const pending = Array.isArray(data) && data.some((c) => !isFinalCommandStatus(c.status))
      return pending ? 2000 : 15000
    },
    refetchIntervalInBackground: false,
  })

export const useMdmLocations = (deviceId, from, to) =>
  useQuery({
    queryKey: ['mdm-locations', deviceId, from, to],
    queryFn: () => mdmApi.getLocations(deviceId, { from, to }),
    enabled: !!deviceId && !!from && !!to,
  })

export const useMdmApps = (deviceId) =>
  useQuery({
    queryKey: ['mdm-apps', deviceId],
    queryFn: () => mdmApi.getApps(deviceId),
    enabled: !!deviceId,
  })

export const useMdmEnrollQr = (enrollLinkId) => {
  const tenantId = getTenantId()
  return useQuery({
    queryKey: ['mdm-enroll-qr', tenantId, enrollLinkId],
    queryFn: () => mdmApi.getEnrollQr(tenantId, enrollLinkId),
    enabled: !!tenantId && !!enrollLinkId,
  })
}

export const useSendMdmCommand = (deviceId) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ type, payload }) => mdmApi.sendCommand(deviceId, type, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mdm-commands', deviceId] })
    },
  })
}

export const useMdmWipe = (deviceId) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (reason) => mdmApi.wipe(deviceId, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mdm-commands', deviceId] }),
  })
}

export const useCancelMdmCommand = (deviceId) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (commandId) => mdmApi.cancelCommand(deviceId, commandId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mdm-commands', deviceId] }),
  })
}

export const useSendMdmBulkCommand = () => {
  const qc = useQueryClient()
  const tenantId = getTenantId()
  return useMutation({
    mutationFn: ({ deviceIds, type, payload }) => mdmApi.sendBulkCommand(tenantId, { deviceIds, type, payload }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mdm-commands'] })
      qc.invalidateQueries({ queryKey: ['mdm-fleet', tenantId] })
    },
  })
}

export const useUpdateMdmPolicy = (deviceId) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (policy) => mdmApi.updatePolicy(deviceId, policy),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mdm-state', deviceId] })
      qc.invalidateQueries({ queryKey: ['mdm-commands', deviceId] })
    },
  })
}

export const useUploadMdmFile = (deviceId) =>
  useMutation({
    mutationFn: ({ file, onProgress }) => mdmApi.uploadFile(deviceId, file, onProgress),
  })

/** Versões publicadas do agente MDM (CONTRACT.md §9). */
export const useMdmAgentVersions = (enabled = true) => {
  const tenantId = getTenantId()
  return useQuery({
    queryKey: ['mdm-agent-versions', tenantId],
    queryFn: () => mdmApi.listAgentVersions(tenantId),
    enabled: !!tenantId && enabled,
    staleTime: 5 * 60_000,
  })
}

/** Versão-alvo definida em cada escopo (grupo/raiz) do tenant. */
export const useMdmAgentTargets = (enabled = true) => {
  const tenantId = getTenantId()
  return useQuery({
    queryKey: ['mdm-agent-targets', tenantId],
    queryFn: () => mdmApi.listAgentTargets(tenantId),
    enabled: !!tenantId && enabled,
  })
}

export const useSetScopeAgentTarget = () => {
  const qc = useQueryClient()
  const tenantId = getTenantId()
  return useMutation({
    mutationFn: ({ groupId, version }) => mdmApi.setScopeAgentTarget(tenantId, { groupId, version }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mdm-agent-targets', tenantId] })
      qc.invalidateQueries({ queryKey: ['mdm-fleet', tenantId] })
      qc.invalidateQueries({ queryKey: ['mdm-state'] })
      qc.invalidateQueries({ queryKey: ['mdm-commands'] })
    },
  })
}

export const useSetDeviceAgentTarget = (deviceId) => {
  const qc = useQueryClient()
  const tenantId = getTenantId()
  return useMutation({
    mutationFn: (version) => mdmApi.setDeviceAgentTarget(deviceId, version),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mdm-state', deviceId] })
      qc.invalidateQueries({ queryKey: ['mdm-commands', deviceId] })
      qc.invalidateQueries({ queryKey: ['mdm-fleet', tenantId] })
    },
  })
}

/** Catálogo de apps gerenciados do tenant (CONTRACT.md §7.1). */
export const useMdmCatalogApps = (enabled = true) => {
  const tenantId = getTenantId()
  return useQuery({
    queryKey: ['mdm-catalog-apps', tenantId],
    queryFn: () => mdmApi.listCatalogApps(tenantId),
    enabled: !!tenantId && enabled,
  })
}

export const useUploadMdmCatalogApp = () => {
  const qc = useQueryClient()
  const tenantId = getTenantId()
  return useMutation({
    mutationFn: ({ file, label, onProgress }) => mdmApi.uploadCatalogApp(tenantId, file, label, onProgress),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mdm-catalog-apps', tenantId] }),
  })
}

export const useDeleteMdmCatalogApp = () => {
  const qc = useQueryClient()
  const tenantId = getTenantId()
  return useMutation({
    mutationFn: (appId) => mdmApi.deleteCatalogApp(tenantId, appId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mdm-catalog-apps', tenantId] }),
  })
}

export const useInstallMdmCatalogApp = () => {
  const qc = useQueryClient()
  const tenantId = getTenantId()
  return useMutation({
    mutationFn: ({ appId, deviceIds, kiosk, allInScope, groupId }) =>
      mdmApi.installCatalogApp(tenantId, appId, { deviceIds, kiosk, allInScope, groupId }),
    onSuccess: (_data, { kiosk }) => {
      qc.invalidateQueries({ queryKey: ['mdm-commands'] })
      qc.invalidateQueries({ queryKey: ['mdm-fleet', tenantId] })
      if (kiosk) qc.invalidateQueries({ queryKey: ['mdm-state'] })
    },
  })
}

/** Política de um grupo (ou da raiz, groupId null) com o que ele herda — CONTRACT.md §11. */
export const useMdmScopePolicy = (groupId, enabled = true) => {
  const tenantId = getTenantId()
  return useQuery({
    queryKey: ['mdm-scope-policy', tenantId, groupId ?? 'root'],
    queryFn: () => mdmApi.getScopePolicy(tenantId, groupId),
    enabled: !!tenantId && enabled,
  })
}

export const useSetMdmScopePolicy = () => {
  const qc = useQueryClient()
  const tenantId = getTenantId()
  return useMutation({
    mutationFn: ({ groupId, policy }) => mdmApi.setScopePolicy(tenantId, { groupId, policy }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mdm-scope-policy', tenantId] })
      qc.invalidateQueries({ queryKey: ['mdm-agent-targets', tenantId] })
      qc.invalidateQueries({ queryKey: ['mdm-state'] })
      qc.invalidateQueries({ queryKey: ['mdm-commands'] })
    },
  })
}

export const useSendMdmScopeCommand = () => {
  const qc = useQueryClient()
  const tenantId = getTenantId()
  return useMutation({
    mutationFn: ({ groupId, type, payload }) => mdmApi.sendScopeCommand(tenantId, { groupId, type, payload }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mdm-commands'] })
      qc.invalidateQueries({ queryKey: ['mdm-fleet', tenantId] })
    },
  })
}

export const useInheritMdmPolicy = (deviceId) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => mdmApi.inheritPolicy(deviceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mdm-state', deviceId] })
      qc.invalidateQueries({ queryKey: ['mdm-commands', deviceId] })
    },
  })
}

/** Histórico de eventos locais do tablet (CONTRACT.md §12.5) + códigos gerados no painel. */
export const useMdmEvents = (deviceId, limit = 100) =>
  useQuery({
    queryKey: ['mdm-events', deviceId, limit],
    queryFn: () => mdmApi.getEvents(deviceId, limit),
    enabled: !!deviceId,
    refetchInterval: visiblePoll(15000),
    refetchIntervalInBackground: false,
  })

/** Gera o código de uso único do técnico (§12.3). `remove` passa pelo MFA step-up (api.js). */
export const useIssueMdmLocalCode = (deviceId) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ purpose, reason }) => mdmApi.issueLocalCode(deviceId, { purpose, reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mdm-events', deviceId] }),
  })
}

/** Sobe a logo do cliente para o tenant (§12.4); a política guarda só fileId + sha256. */
export const useUploadMdmBrandingLogo = () => {
  const tenantId = getTenantId()
  return useMutation({
    mutationFn: (file) => mdmApi.uploadBrandingLogo(tenantId, file),
  })
}

/** Logo do cliente como Blob para a prévia (o arquivo exige Bearer). */
export const useMdmBrandingLogo = (fileId) => {
  const tenantId = getTenantId()
  return useQuery({
    queryKey: ['mdm-branding-logo', tenantId, fileId],
    queryFn: () => mdmApi.downloadBrandingLogo(tenantId, fileId),
    enabled: !!tenantId && !!fileId,
    staleTime: Infinity,
    retry: false,
  })
}
